import {FC, memo} from 'react';

import {Box, Typography, useMediaQuery} from '@mui/material';
import i18n, {t} from 'i18next';

import ChevronRight from 'assets/icons/chevronRight';
import Points from 'assets/icons/points';
import PointsGold from 'assets/icons/pointsGold';
import theme from 'config/theme';
import {useAppSelector} from 'hooks';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {Promotion} from 'types';
import {formatDate, formatPriceWithoutDecimal} from 'utils';

import {
  StyledProPointsSuggestionContainer,
  StyledSectionHeader,
  StyledSectionContentContainer,
  StyledSeeMoreBtn,
  StyledProgressBox,
  StyledProgressBar,
  StyledActivateNowBtn,
  StyledFooterContainer
} from './styles';

const formatPromotionDate = formatDate('DD MMMM YYYY');

const ProPointSuggestionsV2: FC = () => {
  const promotions = useAppSelector((state) => state.basket?.promotions);
  const isMobileView = useMediaQuery(theme.breakpoints.only('mobile'));
  const isReactNativeView = ReactNative.isReactNativeWebView();

  let firstPromo: Promotion | null = null;
  const isThai = i18n.language === 'th';

  if (promotions?.promotionSuggestions?.length) {
    firstPromo = promotions.promotionSuggestions[0]?.promotions[0];
  }
  const campaignTitle = isThai ? firstPromo?.campaignNameTh : firstPromo?.campaignName;

  const navigateToProPointPromotionPage = () => {
    SendBrowserEvent.goToBasketProPointPromotions();
  };

  const openPromotionDetailModal = () => {
    SendBrowserEvent.openProPointPromotionDetailModal(firstPromo);
  };

  const openActivateNowModal = () => {
    SendBrowserEvent.openProPointPromotionActivateNowModal();
  };

  const progress = (firstPromo?.progress ?? 0) / (firstPromo?.conditionDetail?.targetValue ?? 1);

  return (
    <StyledProPointsSuggestionContainer isReactNativeView={isReactNativeView || isMobileView}>
      <StyledSectionHeader>
        <Box display="flex" alignItems="center" gap="4px">
          <Points size={20} />
          <Typography fontWeight={600} lineHeight="24px" color="#212124">
            {t('proPointPromotion.title')}
          </Typography>
        </Box>
        {!!firstPromo && (
          <StyledSeeMoreBtn onClick={navigateToProPointPromotionPage}>
            <Typography fontWeight={600} fontSize="14px" color="#212124">
              {t('proPointPromotion.seeMore')}
            </Typography>
            <ChevronRight />
          </StyledSeeMoreBtn>
        )}
      </StyledSectionHeader>

      {!!firstPromo && (
        <StyledSectionContentContainer onClick={openPromotionDetailModal}>
          <Box display="flex" justifyContent="space-between" alignItems="center" width={'100%'}>
            <Typography fontWeight={600} fontSize="14px" color="#212124">
              {t('proPointPromotion.spendSuggestion', {
                amount: formatPriceWithoutDecimal(firstPromo?.suggestionAmount)
              })}
            </Typography>

            <Typography textAlign="right" fontWeight={600} fontSize="14px" color="#212124">
              <Points size={12} /> {firstPromo?.conditionDetail?.rewardValue}
            </Typography>
          </Box>

          <Typography fontSize="10px" lineHeight="16px" color="#6B6B6B">
            {t('proPointPromotion.unlockBy')} {formatPromotionDate(firstPromo?.endDate)}
          </Typography>

          <StyledProgressBox>
            <span className="progressBar-coin" style={{left: `${Math.min(progress * 100, 98)}%`, top: '1px'}}>
              <PointsGold size={24} />
            </span>
            <StyledProgressBar className="promotion-plp-page" variant="determinate" value={progress * 100} />
          </StyledProgressBox>

          <Box mt="8px" flexBasis="100%" flexDirection={'row'} display="flex" gap="8px" alignItems="center">
            <Typography fontSize="10px" lineHeight="16px" color="#212124">
              {campaignTitle}
            </Typography>
          </Box>
        </StyledSectionContentContainer>
      )}

      <StyledFooterContainer>
        <Typography fontSize="12px" lineHeight="16px" color="#212124" fontWeight={400}>
          {t(firstPromo ? 'proPointPromotion.activatePromotion' : 'proPointPromotion.lookingToActivate')}
        </Typography>
        <StyledActivateNowBtn onClick={openActivateNowModal}>
          <Typography fontSize="12px" lineHeight="16px" fontWeight={600} color="#E82026">
            {t(firstPromo ? 'proPointPromotion.activate' : 'proPointPromotion.activateHere')}
          </Typography>
        </StyledActivateNowBtn>
      </StyledFooterContainer>
    </StyledProPointsSuggestionContainer>
  );
};

export default memo(ProPointSuggestionsV2);
